import React from 'react';
import { Link } from 'react-router-dom';
import { Headphones, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { getAllProducts } from '../data/buildInstructions';

interface BuildInstructionsLandingProps {
    navHeight?: number;
}

const BuildInstructionsLanding: React.FC<BuildInstructionsLandingProps> = ({ navHeight = 104 }) => {
    const products = getAllProducts();

    return (
        <div className="min-h-screen bg-background">
            <div className="container mx-auto px-4" style={{ paddingTop: `${navHeight + 48}px`, paddingBottom: '64px' }}>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.25, 0, 1] }}
                    className="text-center mb-14"
                >
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Headphones className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="font-headline text-black mb-4">
                        Build Your Kibu
                    </h1>
                    <p className="text-lg text-gray-500 max-w-xl mx-auto leading-relaxed font-body">
                        Follow our step-by-step video guide and build your own headphones from scratch.
                    </p>
                </motion.div>

                {/* Products */}
                <div className="max-w-2xl mx-auto space-y-6">
                    {products.map((product, i) => (
                        <motion.div
                            key={product.slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, ease: [0.25, 0.25, 0, 1], delay: 0.1 + i * 0.08 }}
                            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 group"
                        >
                            {/* Product image */}
                            <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
                                {product.thumbnailUrl && (
                                    <img
                                        src={product.thumbnailUrl}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
                            </div>

                            {/* Product info */}
                            <div className="p-6">
                                <h3 className="font-headline text-black mb-2">
                                    {product.name}
                                </h3>
                                <p className="text-gray-500 text-sm font-body leading-relaxed mb-5">
                                    {product.description}
                                </p>

                                {/* Primary CTA */}
                                <Link
                                    to={`/${product.slug}/${product.latestVersion}`}
                                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-body font-medium py-3 px-6 rounded-full transition-all duration-200 hover:shadow-button-hover w-full justify-center"
                                >
                                    Start Building
                                    <ChevronRight className="w-4 h-4" />
                                </Link>

                                {/* Other versions */}
                                {product.versions.length > 1 && (
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <p className="text-xs text-gray-400 font-body mb-2">Other versions:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {product.versions
                                                .filter(v => v.version !== product.latestVersion)
                                                .map((version) => (
                                                    <Link
                                                        key={version.version}
                                                        to={`/${product.slug}/${version.version}`}
                                                        className="text-xs bg-background hover:bg-grey text-gray-500 px-3 py-1 rounded-full transition-colors border border-grey font-body"
                                                    >
                                                        {version.version.toUpperCase()}
                                                    </Link>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BuildInstructionsLanding;
